<script lang="ts" setup>
// -----------------------
// props & emits
// -----------------------
const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
  }>(),
  {
    autofocus: false,
  },
);

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const { productPageUrl, getAssetUrl } = useHelpers();
const { t } = useI18n();

// -----------------------
// types
// -----------------------
interface SearchHit {
  id: string;
  name: string;
  url: string;
  icon?: string;
  image?: string;
  type: string;
  content_type_slug?: string;
}

interface SearchResponse {
  hits: SearchHit[];
  totalHits: number;
}

// -----------------------
// refs
// -----------------------
const searchBoxDiv = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);
const searchTerm = ref("");

// -----------------------
// search state
// -----------------------
const cmsPagesResults = ref<SearchHit[]>([]);
const contentElementsResults = ref<SearchHit[]>([]);
const turatippekResults = ref<SearchHit[]>([]);
const productsResults = ref<SearchHit[]>([]);
const isLoading = ref(false);
const previousSearchTerm = ref("");

// -----------------------
// computed properties
// -----------------------
const hasCmsPagesResults = computed(() => cmsPagesResults.value.length > 0);
const hasContentElementsResults = computed(
  () => contentElementsResults.value.length > 0,
);
const hasTuratippekResults = computed(() => turatippekResults.value.length > 0);
const hasProductsResults = computed(() => productsResults.value.length > 0);

const hasAnyResults = computed(
  () =>
    hasCmsPagesResults.value ||
    hasContentElementsResults.value ||
    hasTuratippekResults.value ||
    hasProductsResults.value,
);

const columnCount = computed(() => {
  let count = 0;
  if (hasCmsPagesResults.value) count++;
  if (hasContentElementsResults.value) count++;
  if (hasTuratippekResults.value) count++;
  if (hasProductsResults.value) count++;
  return count;
});

const gridColsClass = computed(() => {
  switch (columnCount.value) {
    case 1:
      return "md:grid-cols-1";
    case 2:
      return "md:grid-cols-12"; // Use 12-column grid for precise control
    case 3:
      return "md:grid-cols-3";
    case 4:
      return "md:grid-cols-4";
    default:
      return "md:grid-cols-1";
  }
});

const cmsPagesColSpan = computed(() => {
  if (columnCount.value === 2 && hasCmsPagesResults.value) {
    return "md:col-span-4"; // 33% width
  }
  return "";
});

const contentElementsColSpan = computed(() => {
  if (
    columnCount.value === 2 &&
    hasCmsPagesResults.value &&
    hasContentElementsResults.value
  ) {
    return "md:col-span-4"; // 33% width
  }
  if (
    columnCount.value === 2 &&
    hasContentElementsResults.value &&
    hasProductsResults.value
  ) {
    return "md:col-span-4"; // 33% width
  }
  // If turatippek is one of the two columns with products, use similar span logic
  if (
    columnCount.value === 2 &&
    hasContentElementsResults.value &&
    hasTuratippekResults.value
  ) {
    return "md:col-span-4";
  }
  return "";
});

const turatippekColSpan = computed(() => {
  if (
    columnCount.value === 2 &&
    hasCmsPagesResults.value &&
    hasTuratippekResults.value
  ) {
    return "md:col-span-4";
  }
  if (
    columnCount.value === 2 &&
    hasTuratippekResults.value &&
    hasProductsResults.value
  ) {
    return "md:col-span-4";
  }
  if (
    columnCount.value === 2 &&
    hasContentElementsResults.value &&
    hasTuratippekResults.value
  ) {
    return "md:col-span-4";
  }
  return "";
});

const productsColSpan = computed(() => {
  if (columnCount.value === 2 && hasProductsResults.value) {
    return "md:col-span-8"; // 66% width
  }
  return "";
});

// -----------------------
// methods
// -----------------------
const productImage = (item: SearchHit | Record<string, any>) => {
  if (item?.image) {
    return getAssetUrl(item.image);
  }
};

const productUrl = (hit: Record<string, any>) => {
  return productPageUrl({
    name: hit.name,
    sku: hit.url,
    url_code: hit.url_code,
  });
};

const redirectToSearchPage = () => {
  if (searchTerm.value) {
    isOpen.value = false;
    location.href = `/search?query=${encodeURIComponent(searchTerm.value)}`;
  }
};

const searchClasses = computed(() => {
  const classes: string[] = [
    "w-full",
    "h-[40px]",
    "border",
    "border-brand-search",
    "focus:outline-brand-primary",
    "placeholder-brand-body",
  ];

  if (config.public.theme.group === "mountex") {
    classes.push("lg:border-none");
  }

  return classes.join(" ");
});

// Direct MeiliSearch search function
const performSearch = async (query: string) => {
  if (!query.trim()) {
    // Only clear results if search term is completely empty
    cmsPagesResults.value = [];
    contentElementsResults.value = [];
    turatippekResults.value = [];
    productsResults.value = [];
    previousSearchTerm.value = "";
    return;
  }

  // Don't search if it's the same query
  if (previousSearchTerm.value === query.trim()) {
    return;
  }

  isLoading.value = true;

  try {
    // Search for CMS Pages - max 5 results
    const cmsPagesResponse = await $fetch<SearchResponse>(
      `/api/meilisearch/search`,
      {
        method: "POST",
        body: {
          query,
          filter: 'type = "10_cmspage"',
          limit: 5,
        },
      },
    );
    cmsPagesResults.value = cmsPagesResponse.hits || [];

    // Search for Content Elements - max 5 results
    const contentElementsResponse = await $fetch<SearchResponse>(
      `/api/meilisearch/search`,
      {
        method: "POST",
        body: {
          query,
          filter: 'type = "15_contentelement"',
          limit: 5,
        },
      },
    );

    // Split content elements into turatippek and the rest
    const hits = contentElementsResponse.hits || [];
    turatippekResults.value = hits.filter(
      (h) => h.content_type_slug === "turatippek",
    );
    contentElementsResults.value = hits.filter(
      (h) => h.content_type_slug !== "turatippek",
    );

    // Search for Products - max 20 results (for up to 4 columns × 5 products)
    const productsResponse = await $fetch<SearchResponse>(
      `/api/meilisearch/search`,
      {
        method: "POST",
        body: {
          query,
          filter: 'type = "20_masterproduct"',
          limit: 20,
        },
      },
    );
    productsResults.value = productsResponse.hits || [];

    // Update previous search term after successful search
    previousSearchTerm.value = query.trim();
  } catch (error) {
    // Keep previous results visible on error
  } finally {
    isLoading.value = false;
  }
};

// Debounced search function with fallback
let searchTimeout: NodeJS.Timeout | null = null;
const debouncedSearch = (query: string) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    performSearch(query);
  }, 300);
};

// Handle search input
const handleSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newSearchTerm = target.value;
  searchTerm.value = newSearchTerm;
  isOpen.value = true;

  if (newSearchTerm.trim()) {
    debouncedSearch(newSearchTerm);
  } else {
    // Only clear results when search term is completely empty
    cmsPagesResults.value = [];
    contentElementsResults.value = [];
    turatippekResults.value = [];
    productsResults.value = [];
    previousSearchTerm.value = "";
  }
};

// -----------------------
// vue events
// -----------------------
onMounted(() => {
  document.addEventListener("click", handleSearchBoxClickOutside);

  if (props.autofocus) {
    const focusFn = setInterval(() => {
      if (searchInput.value instanceof HTMLInputElement) {
        searchInput.value.focus();
        clearInterval(focusFn);
      }
    }, 100);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleSearchBoxClickOutside);
});

const handleSearchBoxClickOutside = (event: MouseEvent) => {
  const target = event.target as Node | null;
  const el = searchBoxDiv.value as HTMLElement | null;
  if (el && target && !el.contains(target)) {
    isOpen.value = false;
  }
};
</script>

<template>
  <div ref="searchBoxDiv" class="relative">
    <!-- Search Input -->
    <div class="flex">
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="search"
        :placeholder="t('search')"
        :class="searchClasses"
        style="outline-offset: 0px"
        @input="handleSearchInput"
        @keydown.enter="redirectToSearchPage"
      />
      <button
        class="bg-brand-search flex items-center justify-center hover:shadow-md transition-shadow duration-300 ease-in-out"
        :aria-label="t('search')"
        :title="t('search')"
        @click="redirectToSearchPage"
      >
        <i class="m-icon-search text-brand-secondary text-[26px]"></i>
      </button>
    </div>

    <!-- Dropdown with search sections -->
    <div
      v-if="isOpen && searchTerm"
      class="absolute w-full md:w-[960px] bg-white border mr-5 mt-1 overflow-y-auto max-h-[400px] md:max-h-[500px] shadow-lg z-50"
    >
      <!-- No Results Message -->
      <div
        v-if="!hasAnyResults && !isLoading"
        class="p-4 text-center text-gray-500"
      >
        {{ t("searchbox.no_search_results") }}
      </div>

      <!-- Results -->
      <div v-else>
        <!-- Mobile Layout (stacked) -->
        <div class="block md:hidden">
          <!-- Master Products Section -->
          <div v-if="hasProductsResults">
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700"
            >
              {{ t("searchbox.products") }}
            </div>
            <ul>
              <li
                v-for="(hit, hitIndex) in productsResults.slice(
                  0,
                  columnCount === 1 ? 20 : columnCount >= 3 ? 5 : 15,
                )"
                :key="hit.id"
              >
                <a :href="productUrl(hit)" class="flex">
                  <div
                    :class="hitIndex > 0 ? 'border-t border-gray-100' : ''"
                    class="w-full grid grid-cols-12 py-2 px-3 items-center text-sm hover:bg-brand-secondary"
                  >
                    <div
                      class="col-span-9 pr-3 h-full flex hover:underline items-center underline-offset-2"
                    >
                      {{ hit.name }}
                    </div>
                    <div class="col-span-3 text-right">
                      <NuxtImg
                        v-if="productImage(hit)"
                        :src="productImage(hit)"
                        format="webp"
                        loading="lazy"
                        height="100"
                      />
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          <!-- CMS Pages Section -->
          <div v-if="hasCmsPagesResults" class="border-b border-gray-200">
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700"
            >
              {{ t("searchbox.cms_pages") }}
            </div>
            <ul>
              <li v-for="(hit, hitIndex) in cmsPagesResults" :key="hit.id">
                <a :href="`${hit.url}`" class="flex">
                  <div
                    :class="hitIndex > 0 ? 'border-t border-gray-100' : ''"
                    class="w-full grid grid-cols-12 py-2 px-3 items-center text-sm hover:bg-brand-secondary"
                  >
                    <div
                      class="col-span-9 pr-3 flex items-center h-full hover:underline underline-offset-2"
                    >
                      {{ hit.name }}
                    </div>
                    <div class="col-span-3 text-center text-[40px]">
                      <i v-if="hit.icon" :class="hit.icon"></i>
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          <!-- Content Elements Section -->
          <div
            v-if="hasContentElementsResults"
            class="border-b border-gray-200"
          >
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700"
            >
              {{ t("searchbox.content_elements") }}
            </div>
            <ul>
              <li
                v-for="(hit, hitIndex) in contentElementsResults"
                :key="hit.id"
              >
                <a :href="`${hit.url}`" class="flex">
                  <div
                    :class="hitIndex > 0 ? 'border-t border-gray-100' : ''"
                    class="w-full grid grid-cols-12 py-2 px-3 items-center text-sm hover:bg-brand-secondary"
                  >
                    <div
                      class="col-span-9 pr-3 flex items-center h-full hover:underline underline-offset-2"
                    >
                      {{ hit.name }}
                    </div>
                    <div class="col-span-3 text-center text-[40px]">
                      <i v-if="hit.icon" :class="hit.icon"></i>
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          <!-- Turatippek Section (mobile, after content elements) -->
          <div v-if="hasTuratippekResults" class="border-b border-gray-200">
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700"
            >
              {{ t("searchbox.turatippek") }}
            </div>
            <ul>
              <li v-for="(hit, hitIndex) in turatippekResults" :key="hit.id">
                <a :href="`${hit.url}`" class="flex">
                  <div
                    :class="hitIndex > 0 ? 'border-t border-gray-100' : ''"
                    class="w-full grid grid-cols-12 py-2 px-3 items-center text-sm hover:bg-brand-secondary"
                  >
                    <div
                      class="col-span-9 pr-3 flex items-center h-full hover:underline underline-offset-2"
                    >
                      {{ hit.name }}
                    </div>
                    <div class="col-span-3 text-center text-[40px]">
                      <i v-if="hit.icon" :class="hit.icon"></i>
                    </div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Desktop Layout (dynamic grid) -->
        <div class="hidden md:grid md:gap-4 md:p-4" :class="gridColsClass">
          <!-- Products Columns (dynamic) -->
          <template v-if="hasProductsResults">
            <!-- Only products: 4 columns with up to 20 products -->
            <div v-if="columnCount === 1" class="space-y-2">
              <div
                class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
              >
                {{ t("searchbox.products") }}
              </div>
              <div class="grid grid-cols-4 gap-2">
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(0, 5)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(5, 10)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(10, 15)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(15, 20)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- 2-column layout: Products take 66% width with 2 columns inside (up to 10 items) -->
            <div
              v-else-if="columnCount === 2"
              class="space-y-2"
              :class="productsColSpan"
            >
              <div
                class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
              >
                {{ t("searchbox.products") }}
              </div>
              <div class="grid grid-cols-2 gap-2">
                <!-- Left column -->
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(0, 5)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
                <!-- Right column -->
                <div class="space-y-1">
                  <a
                    v-for="hit in productsResults.slice(5, 10)"
                    :key="hit.id"
                    :href="productUrl(hit)"
                    class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-8 h-8 flex-shrink-0">
                        <NuxtImg
                          v-if="productImage(hit)"
                          :src="productImage(hit)"
                          format="webp"
                          loading="lazy"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>
                      <span
                        class="hover:underline underline-offset-2 flex-1 min-w-0"
                        >{{ hit.name }}</span
                      >
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- 3 or 4 column layout: Products take 1 column with up to 5 items -->
            <div v-else class="space-y-2">
              <div
                class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
              >
                {{ t("searchbox.products") }}
              </div>
              <div class="space-y-1">
                <a
                  v-for="hit in productsResults.slice(0, 5)"
                  :key="hit.id"
                  :href="productUrl(hit)"
                  class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
                >
                  <div class="flex items-center space-x-2">
                    <div class="w-8 h-8 flex-shrink-0">
                      <NuxtImg
                        v-if="productImage(hit)"
                        :src="productImage(hit)"
                        format="webp"
                        loading="lazy"
                        class="w-full h-full object-cover rounded"
                      />
                    </div>
                    <span
                      class="hover:underline underline-offset-2 flex-1 min-w-0"
                      >{{ hit.name }}</span
                    >
                  </div>
                </a>
              </div>
            </div>
          </template>

          <!-- CMS Pages Column -->
          <div
            v-if="hasCmsPagesResults"
            class="space-y-2"
            :class="cmsPagesColSpan"
          >
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
            >
              {{ t("searchbox.cms_pages") }}
            </div>
            <div class="space-y-1">
              <a
                v-for="hit in cmsPagesResults"
                :key="hit.id"
                :href="`${hit.url}`"
                class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <i v-if="hit.icon" :class="hit.icon + ' text-gray-400'"></i>
                  <span class="hover:underline underline-offset-2">{{
                    hit.name
                  }}</span>
                </div>
              </a>
            </div>
          </div>

          <!-- Content Elements Column -->
          <div
            v-if="hasContentElementsResults"
            class="space-y-2"
            :class="contentElementsColSpan"
          >
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
            >
              {{ t("searchbox.content_elements") }}
            </div>
            <div class="space-y-1">
              <a
                v-for="hit in contentElementsResults"
                :key="hit.id"
                :href="`${hit.url}`"
                class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <i v-if="hit.icon" :class="hit.icon + ' text-gray-400'"></i>
                  <span class="hover:underline underline-offset-2">{{
                    hit.name
                  }}</span>
                </div>
              </a>
            </div>
          </div>

          <!-- Turatippek Column -->
          <div
            v-if="hasTuratippekResults"
            class="space-y-2"
            :class="turatippekColSpan"
          >
            <div
              class="px-3 py-2 bg-brand-secondary text-sm font-medium text-gray-700 rounded"
            >
              {{ t("searchbox.turatippek") }}
            </div>
            <div class="space-y-1">
              <a
                v-for="hit in turatippekResults"
                :key="hit.id"
                :href="`${hit.url}`"
                class="block p-2 text-sm hover:bg-brand-secondary rounded transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <i v-if="hit.icon" :class="hit.icon + ' text-gray-400'"></i>
                  <span class="hover:underline underline-offset-2">{{
                    hit.name
                  }}</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
